import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  InputAdornment,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';
import MusicVideoIcon from '@mui/icons-material/MusicVideo';
import SmsIcon from '@mui/icons-material/Sms';
import GoogleIcon from '@mui/icons-material/Google';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GroupsIcon from '@mui/icons-material/Groups';
import HubIcon from '@mui/icons-material/Hub';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import api from '../services/api';

const channelIcons = {
  whatsapp: WhatsAppIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  telegram: TelegramIcon,
  email: EmailIcon,
  voip: PhoneInTalkIcon,
  sip: SettingsPhoneIcon,
  tiktok: MusicVideoIcon,
  sms: SmsIcon,
  google_business: GoogleIcon,
  mercadolivre: StorefrontIcon,
  teams: GroupsIcon,
};

const channelColors = {
  whatsapp: '#25D366',
  facebook: '#1877F2',
  instagram: '#E4405F',
  telegram: '#0088CC',
  email: '#FF6B00',
  voip: '#8B5CF6',
  sip: '#06B6D4',
  tiktok: '#000000',
  sms: '#43A047',
  google_business: '#4285F4',
  mercadolivre: '#FFE600',
  teams: '#6264A7',
};

const emptyPlan = {
  channel: '',
  name: '',
  description: '',
  priceMonthly: 0,
  priceYearly: 0,
  maxConnections: 1,
  features: [''],
  isActive: true,
  requiresApproval: false,
  trialDays: 1,
  sortOrder: 0,
};

function formatCurrency(value) {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export default function HubPlansAdmin() {
  const [plans, setPlans] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [acctForm, setAcctForm] = useState({ currentPassword: '', newEmail: '', newPassword: '' });
  const [acctMsg, setAcctMsg] = useState({ type: '', text: '' });
  const [acctSaving, setAcctSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({ ...emptyPlan });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, statsRes] = await Promise.all([
        api.get('/hub-subscriptions/plans'),
        api.get('/hub-subscriptions/admin/stats').catch(() => ({ data: null })),
      ]);
      setPlans(plansRes.data);
      setSubscriptionStats(statsRes.data);
    } catch (err) {
      setError('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNew = () => {
    setEditingPlan(null);
    setForm({
      ...emptyPlan,
      sortOrder: plans.length + 1,
    });
    setError('');
    setSuccess('');
    setDialogOpen(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      channel: plan.channel || '',
      name: plan.name || '',
      description: plan.description || '',
      priceMonthly: Number(plan.priceMonthly) || 0,
      priceYearly: Number(plan.priceYearly) || 0,
      maxConnections: plan.maxConnections || 1,
      features: plan.features?.length > 0 ? [...plan.features] : [''],
      isActive: plan.isActive !== false,
      requiresApproval: plan.requiresApproval || false,
      trialDays: plan.trialDays || 0,
      sortOrder: plan.sortOrder || 0,
    });
    setError('');
    setSuccess('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingPlan(null);
    setError('');
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFeatureChange = (index, value) => {
    setForm((prev) => {
      const features = [...prev.features];
      features[index] = value;
      return { ...prev, features };
    });
  };

  const handleAddFeature = () => {
    setForm((prev) => ({ ...prev, features: [...prev.features, ''] }));
  };

  const handleRemoveFeature = (index) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.channel || !form.name || !form.priceMonthly) {
      setError('Canal, nome e preco mensal sao obrigatorios');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        priceMonthly: Number(form.priceMonthly),
        priceYearly: Number(form.priceYearly) || null,
        maxConnections: Number(form.maxConnections) || 1,
        trialDays: Number(form.trialDays) || 0,
        sortOrder: Number(form.sortOrder) || 0,
        features: form.features.filter((f) => f.trim() !== ''),
      };

      await api.post('/hub-subscriptions/admin/plans', payload);
      setSuccess('Plano salvo com sucesso!');
      setTimeout(() => {
        setDialogOpen(false);
        setSuccess('');
        loadData();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (plan) => {
    setDeleteDialog({ open: true, plan });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.plan) return;
    setDeleting(true);
    try {
      await api.delete(`/hub-subscriptions/admin/plans/${deleteDialog.plan.id}`);
      setDeleteDialog({ open: false, plan: null });
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao remover plano');
      setDeleteDialog({ open: false, plan: null });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await api.post('/hub-subscriptions/admin/plans', {
        ...plan,
        isActive: !plan.isActive,
      });
      loadData();
    } catch (err) {
      setError('Erro ao alterar status');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalActive = subscriptionStats?.totalActive || 0;
  const totalRevenue = subscriptionStats?.monthlyRevenue || 0;

  const handleLogout = () => {
    localStorage.removeItem('mkt_token');
    localStorage.removeItem('mkt_user');
    window.location.href = '/hub/marketplace';
  };

  const handleAcctSave = async () => {
    setAcctMsg({ type: '', text: '' });
    if (!acctForm.currentPassword) {
      setAcctMsg({ type: 'error', text: 'Informe a senha atual.' });
      return;
    }
    if (!acctForm.newEmail && !acctForm.newPassword) {
      setAcctMsg({ type: 'error', text: 'Informe um novo email e/ou nova senha.' });
      return;
    }
    setAcctSaving(true);
    try {
      const { data } = await api.post('/auth/change-credentials', {
        currentPassword: acctForm.currentPassword,
        newEmail: acctForm.newEmail || undefined,
        newPassword: acctForm.newPassword || undefined,
      });
      if (data?.access_token) localStorage.setItem('mkt_token', data.access_token);
      setAcctMsg({ type: 'success', text: 'Credenciais atualizadas com sucesso.' });
      setAcctForm({ currentPassword: '', newEmail: '', newPassword: '' });
    } catch (err) {
      setAcctMsg({
        type: 'error',
        text: err.response?.data?.message || err.response?.data?.error || 'Erro ao atualizar.',
      });
    } finally {
      setAcctSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <img src={`${process.env.PUBLIC_URL}/iconeHUB.png`} alt="OtimizApp" style={{ height: 36, width: 36, objectFit: 'contain', display: 'block' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Administracao de Planos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie canais, precos, trial e features do Hub de Canais
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Sair
          </Button>
          <Button
            variant="outlined"
            startIcon={<ManageAccountsIcon />}
            onClick={() => { setAcctMsg({ type: '', text: '' }); setAcctOpen(true); }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Conta
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Novo Plano
          </Button>
        </Box>
      </Box>

      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1976d2' }}>
                {plans.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Planos Cadastrados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#25D366' }}>
                {plans.filter((p) => p.isActive).length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Planos Ativos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#FF6B00' }}>
                {totalActive}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Assinaturas Ativas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#7C3AED' }}>
                {formatCurrency(totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Receita Mensal
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Plans table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Canal</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Preco Mensal</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Preco Anual</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Conexoes</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Trial</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Features</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => {
              const Icon = channelIcons[plan.channel] || HubIcon;
              const color = channelColors[plan.channel] || '#666';

              return (
                <TableRow
                  key={plan.id}
                  hover
                  sx={{
                    opacity: plan.isActive ? 1 : 0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {plan.sortOrder}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: `${color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon sx={{ fontSize: 20, color }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {plan.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.channel}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(plan.priceMonthly)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {plan.priceYearly ? formatCurrency(plan.priceYearly) : '-'}
                    </Typography>
                    {plan.priceYearly && plan.priceMonthly > 0 && (
                      <Typography variant="caption" color="success.main">
                        {Math.round((1 - plan.priceYearly / (plan.priceMonthly * 12)) * 100)}% desc.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={plan.maxConnections}
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 32 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {plan.trialDays > 0 ? (
                      <Chip
                        label={`${plan.trialDays} dia${plan.trialDays > 1 ? 's' : ''}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={plan.isActive ? 'Clique para desativar' : 'Clique para ativar'}>
                      <Chip
                        label={plan.isActive ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={plan.isActive ? 'success' : 'default'}
                        onClick={() => handleToggleActive(plan)}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={plan.features?.length || 0}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(plan)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(plan)}
                        sx={{ '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Dialog Conta (trocar email/senha) ──────────────── */}
      <Dialog open={acctOpen} onClose={() => setAcctOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Conta do administrador</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Para alterar o email e/ou a senha, confirme a senha atual.
          </Typography>
          {acctMsg.text && (
            <Alert severity={acctMsg.type || 'info'} sx={{ mb: 2 }}>{acctMsg.text}</Alert>
          )}
          <TextField
            fullWidth
            type="password"
            label="Senha atual"
            value={acctForm.currentPassword}
            onChange={(e) => setAcctForm({ ...acctForm, currentPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="email"
            label="Novo email (opcional)"
            value={acctForm.newEmail}
            onChange={(e) => setAcctForm({ ...acctForm, newEmail: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            label="Nova senha (opcional, min. 6)"
            value={acctForm.newPassword}
            onChange={(e) => setAcctForm({ ...acctForm, newPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAcctOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleAcctSave} disabled={acctSaving} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {acctSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog Editar/Criar Plano ────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {editingPlan ? `Editar: ${editingPlan.name}` : 'Novo Plano'}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && dialogOpen && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2.5}>
            {/* Identificacao */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                Identificacao
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Canal (slug) *"
                value={form.channel}
                onChange={(e) => handleChange('channel', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                disabled={!!editingPlan}
                placeholder="ex: whatsapp"
                size="small"
                helperText={editingPlan ? 'Nao editavel' : 'Identificador unico do canal'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nome *"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="WhatsApp Oficial"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ordem"
                type="number"
                value={form.sortOrder}
                onChange={(e) => handleChange('sortOrder', e.target.value)}
                size="small"
                helperText="Posicao no marketplace"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descricao"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Breve descricao do plano para o marketplace"
                size="small"
                multiline
                rows={2}
              />
            </Grid>

            {/* Precos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1.5, mb: 1, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                Precos e Limites
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Preco Mensal *"
                type="number"
                value={form.priceMonthly}
                onChange={(e) => handleChange('priceMonthly', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Preco Anual"
                type="number"
                value={form.priceYearly}
                onChange={(e) => handleChange('priceYearly', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                size="small"
                helperText={
                  form.priceYearly && form.priceMonthly > 0
                    ? `${Math.round((1 - form.priceYearly / (form.priceMonthly * 12)) * 100)}% desconto`
                    : 'Deixe 0 para nao oferecer'
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Max Conexoes"
                type="number"
                value={form.maxConnections}
                onChange={(e) => handleChange('maxConnections', e.target.value)}
                size="small"
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Dias de Trial Gratis"
                type="number"
                value={form.trialDays}
                onChange={(e) => handleChange('trialDays', e.target.value)}
                size="small"
                inputProps={{ min: 0 }}
                helperText="0 = sem trial"
              />
            </Grid>

            {/* Toggles */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1.5, mb: 1, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                Configuracoes
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Ativo</Typography>
                    <Typography variant="caption" color="text.secondary">Visivel no marketplace</Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.requiresApproval}
                    onChange={(e) => handleChange('requiresApproval', e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Requer Aprovacao</Typography>
                    <Typography variant="caption" color="text.secondary">Admin deve aprovar manualmente</Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Features */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                  Features (itens do plano)
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddFeature}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Adicionar
                </Button>
              </Box>
            </Grid>

            {form.features.map((feature, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                  <TextField
                    fullWidth
                    size="small"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFeature(index)}
                    disabled={form.features.length <= 1}
                    sx={{ '&:hover': { color: 'error.main' } }}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Salvando...' : editingPlan ? 'Salvar Alteracoes' : 'Criar Plano'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog Confirmar Delete ─────────────────────── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, plan: null })}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 380 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Remover plano?</DialogTitle>
        <DialogContent>
          <Typography>
            O plano <strong>{deleteDialog.plan?.name}</strong> ({deleteDialog.plan?.channel}) sera removido permanentemente.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Planos com assinaturas ativas nao podem ser removidos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, plan: null })}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
